/**
 * AdminPageHelp — DS primitive rendered under the H1 of every admin page.
 *
 * Answers the "what am I looking at?" question with a consistent 4-question
 * structure so admins can self-serve context without a docs round-trip.
 *
 *   1. What is this page for?
 *   2. What can I configure here?
 *   3. What happens if I change these settings?
 *   4. Can I undo it?
 *
 * Collapsed by default. Compact card, `--ds-*` tokens, dark-mode-round-trip
 * clean, mobile 375 safe.
 *
 * Consolidated in Wave 6 Batch 1: previously a re-export shim from
 * `@/components/v2/AdminPageHelp`. All 36 admin callsites migrated to the
 * DS import path; the v2 source file has been deleted. This file is now
 * the sole source of truth.
 *
 * Props:
 *   - title       : string (H4 inside the card — usually the page's short name)
 *   - purpose     : ReactNode | string  ("What is this page for?")
 *   - configures  : ReactNode | string  ("What can I configure here?")
 *   - impact      : ReactNode | string  ("What happens if I change these settings?")
 *   - safety      : ReactNode | string  ("Can I undo it?")
 *   - defaultOpen : boolean (default false)
 */
import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";

export default function AdminPageHelp({
  title,
  purpose,
  configures,
  impact,
  safety,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <section
      data-testid="admin-page-help"
      className="mb-5 rounded-lg border overflow-hidden"
      style={{
        borderColor: "var(--ds-color-border)",
        background: "var(--ds-color-surface-muted)",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="admin-page-help-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left"
      >
        <span className="flex items-center gap-2">
          <HelpCircle
            className="w-4 h-4"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden="true"
          />
          <span
            className="text-xs uppercase tracking-[0.16em]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Help
          </span>
          {title ? (
            <span
              className="text-sm font-medium"
              style={{ color: "var(--ds-color-ink)" }}
              data-testid="admin-page-help-title"
            >
              · {title}
            </span>
          ) : null}
        </span>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: "var(--ds-color-ink-muted)" }}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <div
          data-testid="admin-page-help-body"
          className="border-t px-4 py-4 space-y-3"
          style={{ borderColor: "var(--ds-color-border)", background: "var(--ds-color-surface)" }}
        >
          <HelpRow question="What is this page for?" answer={purpose} testId="admin-page-help-purpose" />
          <HelpRow question="What can I configure here?" answer={configures} testId="admin-page-help-configures" />
          <HelpRow question="What happens if I change these settings?" answer={impact} testId="admin-page-help-impact" />
          <HelpRow question="Can I undo it?" answer={safety} testId="admin-page-help-safety" />
        </div>
      ) : null}
    </section>
  );
}

/* Also re-export the named binding for any consumer that imports
   `{ AdminPageHelp }` instead of the default (preserves prior shim API). */
export { AdminPageHelp };

function HelpRow({ question, answer, testId }) {
  if (!answer) return null;
  return (
    <div className="text-sm leading-relaxed" data-testid={testId}>
      <div
        className="text-[11px] uppercase tracking-[0.14em] font-medium"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        {question}
      </div>
      <div className="mt-1" style={{ color: "var(--ds-color-ink)" }}>
        {answer}
      </div>
    </div>
  );
}

/* Legacy re-export name for anything that imports `AdminPageHelp` as a
   named import (matches the previous shim contract). */
